'use client'

import React from 'react'
import { parseFormula, molarMassOf, ELEMENT_TABLE } from '@/lib/tools/chemistryCalculators'

type Props = {
    formula: string
}

const WIDTH = 340
const BAR_HEIGHT = 22
const BAR_GAP = 10
const PAD_LEFT = 46
const PAD_RIGHT = 56
const PAD_TOP = 10
const PAD_BOTTOM = 10

const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#a78bfa', '#f59e0b', '#0891b2', '#db2777', '#65a30d']

export default function ElementCompositionChart({ formula }: Props) {
    const counts = parseFormula(formula)
    if (!counts) return null
    const molarMass = molarMassOf(counts)
    if (!molarMass) return null

    const rows = Object.entries(counts)
        .map(([symbol, count]) => {
            const mass = count * ELEMENT_TABLE[symbol].mass
            return { symbol, percent: (mass / molarMass) * 100 }
        })
        .sort((a, b) => b.percent - a.percent)

    const height = PAD_TOP + PAD_BOTTOM + rows.length * (BAR_HEIGHT + BAR_GAP) - BAR_GAP
    const plotWidth = WIDTH - PAD_LEFT - PAD_RIGHT
    const maxPercent = Math.max(...rows.map((r) => r.percent), 1)

    return (
        <svg width={WIDTH} height={height} viewBox={`0 0 ${WIDTH} ${height}`} style={{ maxWidth: '100%', height: 'auto' }}>
            {rows.map((row, i) => {
                const y = PAD_TOP + i * (BAR_HEIGHT + BAR_GAP)
                const barWidth = (row.percent / maxPercent) * plotWidth
                const color = COLORS[i % COLORS.length]
                return (
                    <g key={row.symbol}>
                        <text x={PAD_LEFT - 8} y={y + BAR_HEIGHT / 2 + 4} fontSize={12} textAnchor="end" fill="#374151" fontWeight={600}>
                            {row.symbol}
                        </text>
                        <rect x={PAD_LEFT} y={y} width={Math.max(barWidth, 2)} height={BAR_HEIGHT} rx={3} fill={color} />
                        <text x={PAD_LEFT + barWidth + 6} y={y + BAR_HEIGHT / 2 + 4} fontSize={11} fill="#111827">
                            {row.percent.toFixed(1)}%
                        </text>
                    </g>
                )
            })}
        </svg>
    )
}
